<div class="h-full flex flex-col">
  <div class="flex-1 flex flex-col relative">

    <!-- Error State -->
    <div *ngIf="error$ | async as error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
      <p class="font-semibold">Error:</p>
      <p>{{ error }}</p>
    </div>

    <!-- Toolbar: Period + Sort -->
    <div class="flex items-center justify-end gap-3 mb-3">
      <app-reports-date-range
        [value]="currentDateRange"
        (dateRangeChanged)="onDateRangeChanged($event)">
      </app-reports-date-range>
      <app-reports-sort
        [value]="currentSort"
        [choices]="sortChoices"
        (sortChanged)="onSortChanged($event)">
      </app-reports-sort>
    </div>

    <!-- Content - Always visible to prevent jumping -->
    <div>
      <!-- Mobile Layout (Horizontal scroll only) -->
      <div class="sm:hidden">
        <div class="rounded-xl border border-gray-200 overflow-hidden relative">
          <div class="overflow-x-auto">
            <table class="min-w-full border-b border-gray-200">
              <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Deal</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Stage</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Amount</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Actions</th>
              </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
              <tr *ngIf="loading$ | async">
                <td [attr.colspan]="4" class="px-6 py-12 text-center">
                  <svg class="animate-spin h-6 w-6 text-blue-royal mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </td>
              </tr>
              <tr *ngFor="let deal of paginatedDeals">
                <td class="py-4 pr-3 pl-4 text-sm font-medium whitespace-nowrap text-gray-900">
                  <div>
                    <div class="font-medium">{{ deal.name }}</div>
                    <div class="text-xs text-gray-500">{{ deal.company_name || 'No company' }}</div>
                  </div>
                </td>
                <td class="px-3 py-4 text-sm whitespace-nowrap">
                    <span [ngClass]="getDealStageClass(deal.deal_stage)" class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium">
                      {{ deal.deal_stage }}
                    </span>
                </td>
                <td class="px-3 py-4 text-sm whitespace-nowrap text-gray-900 font-medium">
                  {{ formatCurrency(deal.deal_value) }}
                </td>
                <td class="py-4 pr-4 pl-3">
                  <div class="flex items-center space-x-1">
                    <!-- View -->
                    <button type="button" (click)="viewDeal(deal)" class="bg-gray-100 hover:opacity-90 p-2 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-gray-500">
                        <path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                        <path fill-rule="evenodd" d="M.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" clip-rule="evenodd" />
                      </svg>
                    </button>

                    <!-- Edit -->
                    <button type="button" (click)="editDeal(deal)" class="bg-gray-100 hover:opacity-90 p-2 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-gray-500">
                        <path d="m2.695 14.762-1.262 3.155a.5.5 0 0 0 .65.65l3.155-1.262a4 4 0 0 0 1.343-.886L17.5 5.501a2.121 2.121 0 0 0-3-3L3.58 13.419a4 4 0 0 0-.885 1.343Z" />
                      </svg>
                    </button>

                    <!-- Delete -->
                    <button type="button" (click)="deleteDeal(deal)" class="bg-red-50 hover:opacity-90 p-2 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-red-500">
                        <path fill-rule="evenodd" d="M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z" clip-rule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State Overlay for Mobile -->
          <div *ngIf="!(loading$ | async) && paginatedDeals.length === 0">
            <div class="text-center py-12">
              <h3 class="text-sm font-medium text-gray-900">No deals found</h3>
              <p class="mt-1 text-sm text-gray-500">Get started by adding a new deal.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Desktop Layout (With internal scroll) -->
      <div class="hidden sm:block sm:flex-1 sm:flex sm:flex-col">
        <!-- Table Container with Border -->
        <div class="rounded-xl border border-gray-200 overflow-hidden flex flex-col relative" [style.height.px]="tableHeight">

          <!-- Scrollable Container -->
          <div class="flex-1 overflow-y-scroll custom-scroll">
            <table class="min-w-full divide-y divide-gray-200 border-b border-gray-200">
              <!-- Fixed Header -->
              <thead class="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th scope="col" class="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-700 sm:pl-6 bg-gray-50">Deal</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 bg-gray-50">Company</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 bg-gray-50">Contact</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 bg-gray-50">Deal Stage</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 bg-gray-50">Amount</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 bg-gray-50">Deal Owner</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 bg-gray-50">Date</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-700 bg-gray-50 w-40">Actions</th>
              </tr>
              </thead>
              <!-- Table Body -->
              <tbody class="divide-y divide-gray-200 bg-white">
              <tr *ngIf="loading$ | async">
                <td [attr.colspan]="9" class="px-6 py-12 text-center">
                  <svg class="animate-spin h-6 w-6 text-blue-royal mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </td>
              </tr>
              <tr *ngFor="let deal of paginatedDeals">
                <td class="py-4 pr-3 pl-4 text-sm font-medium whitespace-nowrap text-gray-900 sm:pl-6">
                  <div class="max-w-xs truncate">
                    {{ deal.name }}
                  </div>
                </td>
                <td class="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                  {{ getCompanyName(deal) }}
                </td>
                <td class="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                  {{ getContactName(deal) }}
                </td>
                <td class="px-3 py-4 text-sm whitespace-nowrap">
                    <span [ngClass]="getDealStageClass(deal.deal_stage)" class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium">
                      {{ deal.deal_stage }}
                    </span>
                </td>
                <td class="px-3 py-4 text-sm whitespace-nowrap text-gray-900 font-medium">
                  {{ formatCurrency(deal.deal_value) }}
                </td>
                <td class="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                  <div class="flex items-center">
                    {{ getOwnerName(deal) }}
                  </div>
                </td>
                <td class="px-3 py-4 text-sm whitespace-nowrap text-gray-500">
                  {{ formatDate(deal.close_date) }}
                </td>
                <td class="py-4 pr-4 pl-3 sm:pr-6 w-40">
                  <div class="flex items-center space-x-1">
                    <!-- View -->
                    <button type="button" (click)="viewDeal(deal)" class="bg-gray-100 hover:opacity-90 p-2 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-gray-500">
                        <path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                        <path fill-rule="evenodd" d="M.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" clip-rule="evenodd" />
                      </svg>
                    </button>

                    <!-- Edit -->
                    <button type="button" (click)="editDeal(deal)" class="bg-gray-100 hover:opacity-90 p-2 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-gray-500">
                        <path d="m2.695 14.762-1.262 3.155a.5.5 0 0 0 .65.65l3.155-1.262a4 4 0 0 0 1.343-.886L17.5 5.501a2.121 2.121 0 0 0-3-3L3.58 13.419a4 4 0 0 0-.885 1.343Z" />
                      </svg>
                    </button>

                    <!-- Delete -->
                    <button type="button" (click)="deleteDeal(deal)" class="bg-red-50 hover:opacity-90 p-2 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-red-500">
                        <path fill-rule="evenodd" d="M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z" clip-rule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State Overlay -->
          <div *ngIf="!(loading$ | async) && paginatedDeals.length === 0" class="absolute inset-0 flex items-center justify-center bg-white">
            <div class="text-center">
              <h3 class="text-sm font-medium text-gray-900">No deals found</h3>
              <p class="mt-1 text-sm text-gray-500">Get started by creating a new deal.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div *ngIf="!(loading$ | async) && paginatedDeals.length > 0" class="mt-3 flex items-center justify-between bg-white dark:bg-indigo-custom">
      <app-crm-pagination (perPageChanged)="onPerPageChanged($event)" (pageChanged)="onPageChanged($event)" [pagination]="pagination" class="w-full"></app-crm-pagination>
    </div>

  </div>

  <!-- Add Deal Modal -->
  <app-add-deal-modal
    [isVisible]="showAddDealModal"
    [contacts]="(contactsLookup$ | async) || []"
    [companies]="(companiesLookup$ | async) || []"
    [deal]="selectedDeal"
    [mode]="modalMode"
    (close)="onCloseDealModal()"
    (save)="onSaveDeal($event)">
  </app-add-deal-modal>

  <!-- View Deal Modal -->
  <app-view-deal-modal
    [isVisible]="showViewDealModal"
    (close)="onCloseViewDealModal()">
  </app-view-deal-modal>
</div>
